import React, { useMemo } from 'react';
import {
  Card,
  Typography,
  Grid,
  Box,
  Stack,
  Chip,
  Button,
  IconButton,
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import { registerChart } from './chartSetup.js';
import { SlidersHorizontal, TrendingUp } from 'lucide-react';

registerChart();

function computeMovingAverage(data, windowSize = 5) {
  return data.map((_, idx) => {
    const start = Math.max(0, idx - windowSize + 1);
    const slice = data.slice(start, idx + 1);
    const avg = slice.reduce((acc, curr) => acc + curr, 0) / slice.length;
    return Number(avg.toFixed(2));
  });
}

export const QueuedMessagesChart = ({
  readySamples = [],
  unackedSamples = [],
  totalSamples = [],
  currentReady = 0,
  currentUnacked = 0,
  currentTotal = 0,
  config = { display: 'chart', size: 'medium', range: '10m' },
  onOpenConfig = () => {},
}) => {
  const rangeLabels = {
    '1m': 'last minute',
    '10m': 'last ten minutes',
    '1h': 'last hour',
    '8h': 'last eight hours',
    '1d': 'last day',
  };

  const chartHeights = {
    small: 180,
    medium: 240,
    large: 340,
  };

  const { labels, totalData, readyData, unackedData, yMax, yStepSize } = useMemo(() => {
    const timestampSet = new Set();
    totalSamples.forEach((s) => timestampSet.add(s.timestamp));
    readySamples.forEach((s) => timestampSet.add(s.timestamp));
    unackedSamples.forEach((s) => timestampSet.add(s.timestamp));

    let sortedTimestamps = Array.from(timestampSet).sort((a, b) => a - b);

    if (sortedTimestamps.length === 0) {
      const now = Date.now();
      const rangeSec =
        config.range === '1m' ? 60 :
        config.range === '10m' ? 600 :
        config.range === '1h' ? 3600 :
        config.range === '8h' ? 28800 : 86400;
      const stepSec =
        config.range === '1m' ? 5 :
        config.range === '10m' ? 30 :
        config.range === '1h' ? 60 :
        config.range === '8h' ? 240 : 720;
      for (let t = now - rangeSec * 1000; t <= now; t += stepSec * 1000) {
        sortedTimestamps.push(t);
      }
    }


    const totalMap = new Map(totalSamples.map((s) => [s.timestamp, s.sample]));
    const readyMap = new Map(readySamples.map((s) => [s.timestamp, s.sample]));
    const unackedMap = new Map(unackedSamples.map((s) => [s.timestamp, s.sample]));

    const formatTime = (ts) => {
      const d = new Date(ts);
      const h = d.getHours().toString().padStart(2, '0');
      const m = d.getMinutes().toString().padStart(2, '0');
      const s = d.getSeconds().toString().padStart(2, '0');
      if (config.range === '1m' || config.range === '10m') {
        return `${h}:${m}:${s}`;
      }
      return `${h}:${m}`;
    };

    const formattedLabels = sortedTimestamps.map(formatTime);

    let rawTotal = sortedTimestamps.map((t) => totalMap.get(t) ?? currentTotal);
    let rawReady = sortedTimestamps.map((t) => readyMap.get(t) ?? currentReady);
    let rawUnacked = sortedTimestamps.map((t) => unackedMap.get(t) ?? currentUnacked);

    if (config.display === 'moving_average') {
      rawTotal = computeMovingAverage(rawTotal);
      rawReady = computeMovingAverage(rawReady);
      rawUnacked = computeMovingAverage(rawUnacked);
    }

    const peak = Math.max(0, ...rawTotal, ...rawReady, ...rawUnacked, currentTotal);
    let calculatedMax = 4.0;
    let calculatedStep = 1.0;

    if (peak <= 0.8) {
      calculatedMax = 1.0;
      calculatedStep = 0.5;
    } else if (peak <= 3.5) {
      calculatedMax = Math.ceil(peak) + 1;
      calculatedStep = 1.0;
    } else if (peak <= 8) {
      calculatedMax = Math.ceil(peak * 1.25);
      calculatedStep = 2.0;
    } else if (peak <= 25) {
      calculatedMax = Math.ceil((peak * 1.2) / 5) * 5;
      calculatedStep = 5.0;
    } else {
      calculatedMax = Math.ceil(peak * 1.15);
      calculatedStep = Math.max(1, Math.ceil(calculatedMax / 5));
    }

    return {
      labels: formattedLabels,
      totalData: rawTotal,
      readyData: rawReady,
      unackedData: rawUnacked,
      yMax: calculatedMax,
      yStepSize: calculatedStep,
    };
  }, [
    readySamples,
    unackedSamples,
    totalSamples,
    currentReady,
    currentUnacked,
    currentTotal,
    config.range,
    config.display,
  ]);

  const isMovingAverage = config.display === 'moving_average';

  const createGradient = (ctx, chartArea, r, g, b) => {
    if (!chartArea) return `rgba(${r}, ${g}, ${b}, 0.1)`;
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.25)`);
    gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, 0.04)`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
    return gradient;
  };

  const data = {
    labels,
    datasets: [
      {
        label: isMovingAverage ? 'Total (Moving Avg)' : 'Total',
        data: totalData,
        borderColor: '#ef4444',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 239, 68, 68);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
      {
        label: isMovingAverage ? 'Ready (Moving Avg)' : 'Ready',
        data: readyData,
        borderColor: '#f59e0b',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 245, 158, 11);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
      {
        label: isMovingAverage ? 'Unacked (Moving Avg)' : 'Unacked',
        data: unackedData,
        borderColor: '#0284c7',
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          return createGradient(ctx, chartArea, 2, 132, 199);
        },
        borderWidth: 2,
        tension: 0.35,
        pointRadius: 0,
        pointHoverRadius: 5,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    animation: {
      duration: 300,
      easing: 'easeOutQuart',
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#f8fafc',
        bodyColor: '#e2e8f0',
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#94a3b8',
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: yMax,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          borderDash: [4, 4],
        },
        ticks: {
          stepSize: yStepSize,
          color: '#94a3b8',
          font: { size: 10 },
          callback: (value) => Number(value).toFixed(yMax <= 10 ? 1 : 0),
        },
      },
    },
  };

  return (
    <Card sx={{ p: 2.5 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Queued messages
          </Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={onOpenConfig}
            endIcon={<SlidersHorizontal style={{ width: 12, height: 12, color: '#006951' }} />}
            sx={{
              py: 0.2,
              px: 1,
              fontSize: '0.7rem',
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { borderColor: '#006951', color: '#006951' },
            }}
          >
            {rangeLabels[config.range] || 'last minute'}
          </Button>

          {isMovingAverage && (
            <Chip
              icon={<TrendingUp style={{ width: 12, height: 12, color: '#7c3aed' }} />}
              label="Moving Avg"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: 'rgba(124, 58, 237, 0.1)',
                color: '#7c3aed',
              }}
            />
          )}

          <IconButton
            size="small"
            onClick={onOpenConfig}
            sx={{
              width: 20,
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            ?
          </IconButton>
        </Stack>
      </Stack>

      {/* Main Chart + Stats */}
      <Grid container spacing={3} alignItems="center" sx={{ mt: 1 }}>
        <Grid item xs={12} lg={9}>
          <Box
            sx={{
              height: chartHeights[config.size] || 240,
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {config.display === 'value' ? (
              <Stack alignItems="center" justifyContent="center" height="100%">
                <Typography variant="caption" sx={{ textTransform: 'uppercase', color: 'text.secondary', fontWeight: 700 }}>
                  Current Total Messages
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 900, color: 'text.primary', my: 1 }}>
                  {currentTotal}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 700 }}>
                    Ready: {currentReady}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#0284c7', fontWeight: 700 }}>
                    Unacked: {currentUnacked}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Line data={data} options={options} />
            )}
          </Box>
        </Grid>

        {/* Right side metric cards */}
        <Grid item xs={12} lg={3}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Ready</Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#d97706', fontFamily: 'monospace' }}>
                {currentReady}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#0284c7' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Unacked</Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0284c7', fontFamily: 'monospace' }}>
                {currentUnacked}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Total</Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#dc2626', fontFamily: 'monospace' }}>
                {currentTotal}
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};
